import type { StaffImportJob, StaffImportProgress } from "@lafayette/schemas";

import {
  blockerMessages,
  type OutcomeView,
  outcomeViews,
  pendingOutcomeView,
  processingViews,
} from "./labels";

// What the review page shows for a job, worked out from the job and from R7. Pure functions.

/** `draft` and `closed` have no review screen. `failed` is a job that stopped on its own. */
export type JobPhase =
  | "draft"
  | "validating"
  | "review"
  | "processing"
  | "done"
  | "failed"
  | "closed";

export function jobPhase(job: Pick<StaffImportJob, "status" | "failureCode">): JobPhase {
  switch (job.status) {
    case "UPLOADED":
    case "MAPPING":
      return "draft";
    case "VALIDATING":
      return "validating";
    case "REVIEW_REQUIRED":
      return "review";
    case "APPROVED":
    case "PROCESSING":
      return "processing";
    case "COMPLETED":
      return "done";
    case "FAILED":
      return job.failureCode === "REJECTED_BY_STAFF" ? "closed" : "failed";
  }
}

/** R7 is polled while the server still works on the job. */
export const isWorking = (phase: JobPhase): boolean =>
  phase === "validating" || phase === "processing";

/** A failed job that staff may retry (W10): it stopped on its own, not because staff closed it. */
export const canRetryJob = (job: Pick<StaffImportJob, "status" | "failureCode">): boolean =>
  job.status === "FAILED" &&
  (job.failureCode === "PROCESSING_ERROR" || job.failureCode === "VALIDATION_ABORTED");

/** The address of the page a job opens in the console. Drafts and closed drafts open the wizard. */
export function jobAddress(job: Pick<StaffImportJob, "id" | "status" | "failureCode">): string {
  const base = `/imports/${encodeURIComponent(job.id)}`;
  const phase = jobPhase(job);
  return phase === "draft" || phase === "closed" ? base : `${base}/review`;
}

export type CountPart = Readonly<{ key: string; count: number; view: OutcomeView }>;
export type Reconciliation = Readonly<{
  total: number;
  parts: readonly CountPart[];
  sum: number;
  /** Every row is in exactly one part. */
  reconciled: boolean;
}>;

const reconciliation = (total: number, parts: readonly CountPart[]): Reconciliation => {
  const sum = parts.reduce((all, part) => all + part.count, 0);
  return { total, parts, sum, reconciled: sum === total };
};

/** After approval: `rowCount = created + updated + skipped + invalid + failed (+ waiting + retrying)`. */
export function processingReconciliation(progress: StaffImportProgress): Reconciliation {
  const { processing, counts, job } = progress;
  return reconciliation(job.rowCount, [
    { key: "created", count: processing.created, view: processingViews.CREATED },
    { key: "updated", count: processing.updated, view: processingViews.UPDATED },
    { key: "skipped", count: processing.skipped, view: processingViews.SKIPPED },
    { key: "invalid", count: counts.invalid, view: outcomeViews.INVALID },
    { key: "failed", count: processing.failed, view: processingViews.FAILED },
    { key: "retrying", count: processing.retrying, view: processingViews.RETRYING },
    { key: "waiting", count: processing.waiting, view: processingViews.WAITING },
  ]);
}

/** During review: every row has one outcome (or none yet). */
export function outcomeReconciliation(progress: StaffImportProgress): Reconciliation {
  const { counts, job } = progress;
  return reconciliation(job.rowCount, [
    { key: "pending", count: counts.pending, view: pendingOutcomeView },
    { key: "ready", count: counts.ready, view: outcomeViews.READY },
    { key: "warning", count: counts.warning, view: outcomeViews.WARNING },
    { key: "duplicate", count: counts.duplicate, view: outcomeViews.DUPLICATE_CANDIDATE },
    { key: "invalid", count: counts.invalid, view: outcomeViews.INVALID },
    { key: "approved", count: counts.approved, view: outcomeViews.APPROVED },
    { key: "imported", count: counts.imported, view: outcomeViews.IMPORTED },
    { key: "skipped", count: counts.skipped, view: outcomeViews.SKIPPED },
  ]);
}

export const blockerSentences = (progress: StaffImportProgress): readonly string[] =>
  progress.approval.blockers.map((blocker) => blockerMessages[blocker.code](blocker.count));

/** Every row is skipped or invalid: there is nothing to approve. Staff may close the import. */
export const nothingToImport = (progress: StaffImportProgress): boolean =>
  progress.job.status === "REVIEW_REQUIRED" &&
  progress.job.rowCount > 0 &&
  progress.counts.approved === 0 &&
  progress.counts.pending + progress.counts.ready + progress.counts.warning === 0 &&
  progress.counts.duplicate === 0 &&
  progress.counts.invalid === 0;
